/* eslint-disable react/jsx-no-undef */
/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button } from "reactstrap";
import { RootState } from "../../../reducers";
import { CATEGORIES_WITH_CHECK_BUTTON, LAYER_TAB_CATEGORIES, LAYER_TABS } from "../../../actions/Layers/constants";
import useConfig from "../../../actions/Tenants/config/configHook";
import { CONFIG_TYPES } from "../../../actions/Tenants/config/constants";
import { breakpoints } from "../../../constants/mediaqueries";
import { useIsMobile, useUserPreferences } from "../../../hooks";
import { getFilledArrayOrDefault } from "../../../utils";
import { useRegisterDebugTargets } from "../../../utils/useDebugTargets";
import FullPageLayout from "../Layout/FullPageLayout";
import { MailboxTab } from "../Mailbox/Mailbox";
import { PackingListTab } from "../PackingList/PackingListList";
import RipeningClassDistribution, { RipeningFieldSelector } from "../Ripening/RipeningPlanning/RipeningClassDistribution";
import AssignedTeamFilter from "./AssignedTeamFilter";
import FacilityFilter from "./FacilityFilter";
import FindLayerBox from "./FindLayerBox";
import FruitFilter from "./FruitFilter";
import LayerList from "./LayerList";
import ScanLayerButton from "./ScanLayerButton";
import StagesTabs from "./StagesTabs";
import TabActions from "./TabActions";
import { SwitchTabProvider, useSwitchTabContext } from "./fields/useSwitchTab";

export default function WrappedLayerStart() {
    return (
        <SwitchTabProvider>
            <LayerStart />
        </SwitchTabProvider>
    );
}

function LayerListTab() {
    return (
        <>
            <TabActions />
            <LayerList />
        </>
    );
}

export enum GLOBAL_FILTERS_ON_TABS {
    FRUIT_TYPE = "fruit_type",
    FACILITY = "facility",
    ASSIGNED_TEAM = "assigned_team",
}

const TabComponents = {
    [LAYER_TABS.PACKING_LIST]: PackingListTab,
    [LAYER_TABS.QC_MAILBOX]: MailboxTab,
};

export function LayerStart() {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const user = useSelector((state: RootState) => state.auth.user);
    const config = useConfig();
    const tenant_id = (user as any)?.tenant?.tenant_id;

    // * Register stage debug target
    const tab_config = params.active_tab ? config.configs.find((c) => c.value === params.active_tab && c.type === CONFIG_TYPES.STAGE) : null;
    useRegisterDebugTargets("stage", tenant_id && tab_config ? [{
        id: `stage_${tab_config.id}`,
        url: `/tenants/${tenant_id}/config/${tab_config.id}`,
        text: `Tab ${tab_config.config.text} config`,
    }, {
        id: `stage_edit_${tenant_id}`,
        url: `/tenants/${tenant_id}/tab/${CONFIG_TYPES.STAGE}`,
        text: `Edit tabs`,
    }] : []);

    const globalFilters = getFilledArrayOrDefault(config.root_config.global_filters_on_tabs, [GLOBAL_FILTERS_ON_TABS.FRUIT_TYPE]);

    const [, updatePreferences] = useUserPreferences();
    const isMobile = useIsMobile();

    const { tab_category, sameCategoryTabs, switchTab, activeTab } = useSwitchTabContext();

    const hasScannerButton = useMemo(() => tab_category && getFilledArrayOrDefault(config.root_config.tab_categories_with_check_button, CATEGORIES_WITH_CHECK_BUTTON).includes(tab_category as LAYER_TAB_CATEGORIES), [tab_category, config.root_config.tab_categories_with_check_button]);
    const isHomePage = useMemo(() => location.pathname === "/", [location.pathname]);

    const tabContent = useMemo(() => {
        const CustomComponent = TabComponents[activeTab?.value as string];

        if (!CustomComponent) {
            return <LayerListTab />;
        }

        return <CustomComponent />;
    }, [activeTab?.value]);

    const isDisplayingOverviewTable = !isMobile || !isHomePage;

    return (
        <FullPageLayout>
            {isDisplayingOverviewTable && (

                <div
                    className="w-100 d-flex mx-3n"
                    css={css`
                            flex-wrap: wrap;
                            gap: 1rem;
                            justify-content: space-between;
                            align-items: flex-start;
                            @media (max-width: ${breakpoints[2]}px) {
                                flex-direction: column;
                                align-items: stretch;
                            }
                        `}
                >
                    <div
                        className="d-flex"
                        css={css`
                                gap: 1rem;
                                align-items: flex-end;
                                @media (max-width: ${breakpoints[4]}px) {
                                    width: 100%;
                                    flex-direction: column;
                                    align-items: stretch;
                                }
                            `}
                    >
                        <div css={css`
                                display: none;
                                width: 100%;
                                @media (max-width: ${breakpoints[4]}px) {
                                    display: block;
                                }
                            `}>
                            <h4 className="mb-2">Quick actions</h4>
                        </div>
                        <div css={css`
                                display: flex;
                                align-items: flex-end;
                                gap: 1rem;
                                @media (max-width: 490px) {
                                    flex-direction: column;
                                    width: 100%;
                                }
                                @media (max-width: ${breakpoints[2]}px) {
                                    width: 100%;
                                    justify-content: flex-start;
                                }
                            `}>
                            {hasScannerButton && <ScanLayerButton modal />}
                            <FindLayerBox />
                        </div>
                    </div>
                    <div
                        className="d-flex"
                        css={css`
                                gap: 1rem;
                                flex-wrap: wrap;
                                align-items: flex-end;
                                @media (max-width: ${breakpoints[4]}px) {
                                    width: 100%;
                                    flex-direction: column;
                                    align-items: stretch;
                                }
                            `}
                    >
                        <div css={css`
                                display: none;
                                @media (max-width: ${breakpoints[4]}px) {
                                    display: block;
                                    width: 100%;
                                }
                            `}>
                            <h4 className="mt-5 mb-2">Global filters</h4>
                        </div>
                        <div css={css`
                                display: flex;
                                flex-direction: column;
                                align-items: flex-end;
                                gap: 0.25rem;
                            `}>
                            <div css={css`
                                    display: flex;
                                    align-items: flex-end;
                                    gap: 1rem;
                                    flex-wrap: wrap;
                                    @media (max-width: ${breakpoints[2]}px) {
                                        width: 100%;
                                        justify-content: flex-start;
                                    }
                                `}>
                                {activeTab?.show_class_distribution && <RipeningFieldSelector/>}
                                {globalFilters.includes(GLOBAL_FILTERS_ON_TABS.FRUIT_TYPE) && <FruitFilter />}
                                {globalFilters.includes(GLOBAL_FILTERS_ON_TABS.FACILITY) && <FacilityFilter />}
                                {globalFilters.includes(GLOBAL_FILTERS_ON_TABS.ASSIGNED_TEAM) && <AssignedTeamFilter />}
                            </div>
                            {(globalFilters.includes(GLOBAL_FILTERS_ON_TABS.FRUIT_TYPE) || globalFilters.includes(GLOBAL_FILTERS_ON_TABS.FACILITY) || globalFilters.includes(GLOBAL_FILTERS_ON_TABS.ASSIGNED_TEAM)) && (
                                <button
                                    type="button"
                                    onClick={() => updatePreferences({ fruit_types: [], facilities: null, assigned_team: null })}
                                    css={css`
                                        background: none;
                                        border: none;
                                        padding: 0;
                                        color: #13a55f;
                                        font-size: 0.85rem;
                                        cursor: pointer;
                                        position: relative;
                                        z-index: 1001;
                                        &:hover {
                                            text-decoration: underline;
                                            color: #0f8a4f;
                                        }
                                    `}
                                >
                                    Reset filters
                                </button>
                            )}
                        </div>
                    </div>
                </div>

            )}

            {(activeTab?.show_class_distribution) && <RipeningClassDistribution />}

            {(isMobile && isHomePage && hasScannerButton) && (
                <div className="pt-5 d-flex flex-column gap-5 align-items-center">
                    <ScanLayerButton />

                    <div className="border-top border-bottom py-3">
                        <Button
                            color="link"
                            onClick={() => navigate("/layer")}
                        >
                            Or go to the overview table
                        </Button>
                    </div>
                </div>
            )}

            {isDisplayingOverviewTable && (
                <div className="pt-5">

                    <StagesTabs sameCategoryTabs={sameCategoryTabs} switchTab={switchTab} activeTab={activeTab}>
                        {tabContent}
                    </StagesTabs>

                </div>
            )}
        </FullPageLayout>

    );
}
